import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
  type RefObject,
} from 'react';
import { PauseIcon, PlayIcon, WaveformIcon } from '../components/icons';
import { Banner, cx } from '../components/ui';
import { timecode } from '../lib/format';
import { useHotkeys } from '../lib/useHotkeys';
import { waveformEnvelope } from '../lib/waveform';
import type { Playback, Reaction } from '../lib/types';
import { SPEEDS } from './Player';
import { ReactionMarkers } from './ReactionMarkers';
import { ReactionPops } from './ReactionPops';

/**
 * Playback for a voice note.
 *
 * No video element, no hls.js: an audio note has one progressive AAC/MP4 file
 * and nothing else, so reaching for the adaptive ladder would be loading half
 * a megabyte of JavaScript to play a file the browser can already play.
 *
 * The design brief for this screen is "deliberate, not a video player with the
 * picture missing". So the surface is the waveform: a stable, seeded motif
 * that doubles as the scrubber, filling with chalk as it plays. Every control
 * that matters on the video player is here and behaves the same way, minus
 * full screen, which would be a black rectangle.
 *
 * `playback.captionsUrl` is deliberately not attached. The transcript is still
 * produced for an audio note, but a `<track>` on an audio element has nowhere
 * to render: browsers paint captions over video, and there is no video. A
 * transcript panel is the right home for it and does not exist yet.
 */

const BAR_COUNT = 72;

export interface AudioPlayerProps {
  playback: Playback;
  title: string;
  audioRef: RefObject<HTMLAudioElement | null>;
  /** Fires roughly every 250ms while playing. */
  onProgress?: (currentTime: number, duration: number) => void;
  onEnded?: () => void;
  /** The API's duration, used until the file's own metadata arrives. */
  durationHint?: number | null;
  /**
   * The same timecoded reactions the video player draws. A voice note is
   * reacted to for the same reason a screen recording is, and the waveform is
   * a timeline like any other, so the marker rail and the pops both apply.
   */
  reactions?: readonly Reaction[];
}

const NO_REACTIONS: readonly Reaction[] = [];

export function AudioPlayer({
  playback,
  title,
  audioRef,
  onProgress,
  onEnded,
  durationHint,
  reactions = NO_REACTIONS,
}: AudioPlayerProps) {
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [rate, setRate] = useState(1);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [error, setError] = useState<string | null>(null);

  const envelope = useMemo(() => waveformEnvelope(title, BAR_COUNT), [title]);

  // ---- source ---------------------------------------------------------------
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    setError(null);

    if (playback.mp4Url) {
      audio.src = playback.mp4Url;
      return;
    }
    setError('There is no playable version of this audio note yet.');
  }, [audioRef, playback]);

  // ---- element events -------------------------------------------------------
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onLoaded = () => setDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
    const onTime = () => {
      setCurrentTime(audio.currentTime);
      onProgress?.(audio.currentTime, audio.duration);
      if (audio.buffered.length > 0) {
        setBuffered(audio.buffered.end(audio.buffered.length - 1));
      }
    };
    const onPlay = () => {
      setPlaying(true);
      setError(null);
    };
    const onPause = () => setPlaying(false);
    const onRate = () => setRate(audio.playbackRate);
    const onVolume = () => {
      setMuted(audio.muted);
      setVolume(audio.volume);
    };
    const onEnd = () => {
      setPlaying(false);
      onEnded?.();
    };
    const onFail = () =>
      setError('This audio note would not play. It may still be processing. Try reloading.');

    audio.addEventListener('loadedmetadata', onLoaded);
    audio.addEventListener('durationchange', onLoaded);
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ratechange', onRate);
    audio.addEventListener('volumechange', onVolume);
    audio.addEventListener('ended', onEnd);
    audio.addEventListener('error', onFail);

    return () => {
      audio.removeEventListener('loadedmetadata', onLoaded);
      audio.removeEventListener('durationchange', onLoaded);
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ratechange', onRate);
      audio.removeEventListener('volumechange', onVolume);
      audio.removeEventListener('ended', onEnd);
      audio.removeEventListener('error', onFail);
    };
  }, [audioRef, onEnded, onProgress]);

  // ---- commands -------------------------------------------------------------
  const toggle = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      void audio.play().catch((cause: unknown) => {
        // A source change under a pending play, not a refusal.
        if (cause instanceof DOMException && cause.name === 'AbortError') return;
        setError('Your browser blocked playback. Press play again.');
      });
    } else {
      audio.pause();
    }
  }, [audioRef]);

  const seekBy = useCallback(
    (delta: number) => {
      const audio = audioRef.current;
      if (!audio) return;
      audio.currentTime = Math.max(0, Math.min(audio.duration || 0, audio.currentTime + delta));
    },
    [audioRef],
  );

  const seekToFraction = useCallback(
    (fraction: number) => {
      const audio = audioRef.current;
      if (!audio || !Number.isFinite(audio.duration)) return;
      audio.currentTime = audio.duration * fraction;
    },
    [audioRef],
  );

  const setSpeed = useCallback(
    (value: number) => {
      const audio = audioRef.current;
      if (audio) audio.playbackRate = value;
    },
    [audioRef],
  );

  /** Used by the reaction markers. Clamped so a marker cannot seek past the end. */
  const seekTo = useCallback(
    (seconds: number) => {
      const audio = audioRef.current;
      if (!audio) return;
      const limit = Number.isFinite(audio.duration) ? audio.duration : seconds;
      audio.currentTime = Math.max(0, Math.min(limit, seconds));
      setCurrentTime(audio.currentTime);
    },
    [audioRef],
  );

  // The same map as the video player, minus F: there is nothing to fill a
  // screen with. Muscle memory should carry between the two.
  useHotkeys({
    space: toggle,
    k: toggle,
    arrowleft: () => seekBy(-5),
    arrowright: () => seekBy(5),
    j: () => seekBy(-10),
    l: () => seekBy(10),
    m: () => {
      const audio = audioRef.current;
      if (audio) audio.muted = !audio.muted;
    },
    '0': () => seekToFraction(0),
    '1': () => seekToFraction(0.1),
    '2': () => seekToFraction(0.2),
    '3': () => seekToFraction(0.3),
    '4': () => seekToFraction(0.4),
    '5': () => seekToFraction(0.5),
    '6': () => seekToFraction(0.6),
    '7': () => seekToFraction(0.7),
    '8': () => seekToFraction(0.8),
    '9': () => seekToFraction(0.9),
  });

  const onScrub = (event: ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = Number(event.target.value);
    setCurrentTime(Number(event.target.value));
  };

  // The file's own metadata wins once it lands; until then the API's number
  // keeps the readout from sitting at `--:--` on a slow connection.
  const shownDuration = duration > 0 ? duration : (durationHint ?? 0);
  const playedRatio = shownDuration > 0 ? currentTime / shownDuration : 0;
  const bufferedRatio = shownDuration > 0 ? buffered / shownDuration : 0;
  const playedBars = Math.round(playedRatio * BAR_COUNT);
  const bufferedBars = Math.round(bufferedRatio * BAR_COUNT);

  return (
    <div className="space-y-3">
      <div className="relative overflow-hidden rounded-md border border-hairline bg-slate-900">
        <div className="clapper-stripes absolute inset-x-0 top-0 h-1.5 opacity-[0.14]" aria-hidden="true" />

        {/* No `controls`: an audio element without them renders nothing, which
            is what we want; the chrome below is the player. */}
        <audio ref={audioRef} preload="metadata" />

        <div className="flex flex-col gap-5 px-4 pb-4 pt-8 sm:px-6 sm:pb-5">
          <div className="flex items-center gap-3 text-chalk-faint">
            <WaveformIcon size={16} />
            <span className="text-micro uppercase tracking-[0.1em]">Audio note</span>
          </div>

          {/* Above the waveform, for the same reason it sits above the video's
              scrub bar: the waveform is the seek control here. */}
          <ReactionMarkers
            reactions={reactions}
            durationSec={shownDuration}
            onSeek={seekTo}
            className="-mb-2 h-[15px]"
          />

          {/* The waveform is the scrubber. The range input sits on top of it,
              invisible, so keyboard and assistive tech get a real slider and
              everyone else gets something worth looking at. */}
          <div className="relative h-24 sm:h-28">
            <div className="flex h-full w-full items-center gap-[3px]" aria-hidden="true">
              {envelope.map((height, index) => (
                <span
                  key={index}
                  className={cx(
                    'w-full max-w-[7px] flex-1 rounded-full transition-colors duration-120',
                    index < playedBars
                      ? 'bg-chalk'
                      : index < bufferedBars
                        ? 'bg-chalk/30'
                        : 'bg-slate-700',
                  )}
                  style={{ height: `${height * 100}%`, minHeight: '3px' }}
                />
              ))}
            </div>
            <input
              type="range"
              min={0}
              max={shownDuration || 0}
              step={0.1}
              value={currentTime}
              onChange={onScrub}
              disabled={shownDuration <= 0}
              aria-label="Seek"
              aria-valuetext={`${timecode(currentTime)} of ${timecode(shownDuration)}`}
              className="absolute inset-0 h-full w-full cursor-pointer appearance-none bg-transparent opacity-0 disabled:cursor-default"
            />
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={toggle}
              aria-label={playing ? 'Pause' : `Play ${title}`}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-chalk text-slate-950 transition-colors duration-120 hover:bg-white active:bg-chalk-dim"
            >
              {playing ? <PauseIcon size={18} /> : <PlayIcon size={18} className="ml-0.5" />}
            </button>

            <span className="slate-type text-caption text-chalk" data-timecode>
              {timecode(currentTime)}{' '}
              <span className="text-chalk-faint">/ {timecode(shownDuration)}</span>
            </span>

            <div className="ml-auto flex items-center gap-2">
              <label className="hidden items-center gap-1.5 sm:flex">
                <span className="sr-only">Volume</span>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.05}
                  value={muted ? 0 : volume}
                  onChange={(event) => {
                    const audio = audioRef.current;
                    if (!audio) return;
                    audio.volume = Number(event.target.value);
                    audio.muted = Number(event.target.value) === 0;
                  }}
                  className="h-1 w-16 cursor-pointer appearance-none rounded-full bg-chalk/30 accent-chalk"
                />
              </label>

              <select
                value={rate}
                onChange={(event) => setSpeed(Number(event.target.value))}
                aria-label="Playback speed"
                className="h-7 rounded-xs border border-chalk/20 bg-slate-950/70 px-1.5 font-mono text-micro tnum text-chalk"
              >
                {SPEEDS.map((speed) => (
                  <option key={speed} value={speed}>
                    {speed}×
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <ReactionPops
          reactions={reactions}
          currentTime={currentTime}
          className="absolute right-3 top-7 z-10 sm:right-5"
        />
      </div>

      {error ? <Banner tone="danger" title="Playback problem">{error}</Banner> : null}

    </div>
  );
}
